import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { fmt, today } from "@/lib/billing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

const GST = [0, 5, 12, 18, 28];
const newLine = () => ({ item_id: "", description: "", quantity: "1", rate: "", gst_pct: "18" });

export function LineItemsEditor({ lines, setLines, items, prefix = "inv" }) {
  const upd = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const subtotal = lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.rate) || 0), 0);
  const gst = lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.rate) || 0) * (parseFloat(l.gst_pct) || 0) / 100, 0);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between"><Label>Items</Label>
        <Button type="button" size="sm" variant="outline" data-testid={`${prefix}-add-line-btn`} onClick={() => setLines([...lines, newLine()])} className="h-7 gap-1 border-border"><Plus className="h-3 w-3" /> Line</Button></div>
      {items.length === 0 && <p className="text-xs text-amber-400">No items found. Add items (material / service / rent) in Items & Stock first.</p>}
      <div className="space-y-2">
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end rounded-lg border border-border/60 p-2" data-testid={`${prefix}-line-${i}`}>
            <div className="col-span-12 sm:col-span-4 space-y-1"><Label className="text-[10px] uppercase">Item</Label>
              <Select value={l.item_id} onValueChange={(v) => { const it = items.find((x) => x.id === v); upd(i, { item_id: v, description: l.description || "" , rate: l.rate || "" }); if (it && !l.description) upd(i, { item_id: v, description: it.category || "" }); }}>
                <SelectTrigger data-testid={`${prefix}-line-item-${i}`} className="h-9"><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent>{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name} · {it.item_type || "material"} ({it.unit})</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="col-span-4 sm:col-span-2 space-y-1"><Label className="text-[10px] uppercase">Qty</Label>
              <Input data-testid={`${prefix}-line-qty-${i}`} className="h-9" type="number" step="0.01" min="0" value={l.quantity} onChange={(e) => upd(i, { quantity: e.target.value })} /></div>
            <div className="col-span-4 sm:col-span-2 space-y-1"><Label className="text-[10px] uppercase">Rate</Label>
              <Input data-testid={`${prefix}-line-rate-${i}`} className="h-9" type="number" step="0.01" min="0" value={l.rate} onChange={(e) => upd(i, { rate: e.target.value })} /></div>
            <div className="col-span-3 sm:col-span-2 space-y-1"><Label className="text-[10px] uppercase">GST %</Label>
              <Select value={String(l.gst_pct)} onValueChange={(v) => upd(i, { gst_pct: v })}>
                <SelectTrigger data-testid={`${prefix}-line-gst-${i}`} className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{GST.map((g) => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="col-span-1 sm:col-span-2 flex justify-end items-center gap-2">
              <span className="hidden sm:block text-xs font-mono-sku text-muted-foreground">{fmt((parseFloat(l.quantity) || 0) * (parseFloat(l.rate) || 0) * (1 + (parseFloat(l.gst_pct) || 0) / 100))}</span>
              <Button type="button" size="icon" variant="ghost" onClick={() => setLines(lines.filter((_, j) => j !== i))} className="h-8 w-8 text-rose-400 hover:bg-rose-500/10" disabled={lines.length === 1}><Trash2 className="h-4 w-4" /></Button></div>
            <div className="col-span-12 space-y-1"><Input data-testid={`${prefix}-line-desc-${i}`} className="h-8 text-xs" value={l.description} onChange={(e) => upd(i, { description: e.target.value })} placeholder="Description (optional)" /></div>
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-6 text-sm pt-1">
        <span className="text-muted-foreground">Subtotal <b className="text-foreground font-mono-sku">{fmt(subtotal)}</b></span>
        <span className="text-muted-foreground">GST <b className="text-foreground font-mono-sku">{fmt(gst)}</b></span>
        <span className="text-muted-foreground">Total <b className="text-sky-400 font-mono-sku" data-testid={`${prefix}-grand-total`}>{fmt(subtotal + gst)}</b></span>
      </div>
    </div>
  );
}

export function useBillingData() {
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const reload = () => {
    api.get("/parties").then((r) => setParties(r.data)).catch(() => {});
    api.get("/items").then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { reload(); }, []);
  return { parties, items, reload };
}

export function InvoiceHeaderFields({ f, setF, parties, partyKey, partyLabel, prefix, autoNo }) {
  return (
    <>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2"><Label>Invoice No {autoNo && <span className="text-muted-foreground text-xs">(auto if blank)</span>}</Label>
          <Input data-testid={`${prefix}-no-input`} value={f.invoice_no} onChange={(e) => setF({ ...f, invoice_no: e.target.value })} placeholder={autoNo ? "INV-0001" : "Vendor bill no."} required={!autoNo} /></div>
        <div className="space-y-2"><Label>Date</Label>
          <Input data-testid={`${prefix}-date-input`} type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
        <div className="space-y-2"><Label>Due Date</Label>
          <Input data-testid={`${prefix}-due-input`} type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></div>
      </div>
      <div className="space-y-2"><Label>{partyLabel}</Label>
        <Select value={f[partyKey]} onValueChange={(v) => setF({ ...f, [partyKey]: v })}>
          <SelectTrigger data-testid={`${prefix}-party-select`}><SelectValue placeholder={`Select ${partyLabel.toLowerCase()}`} /></SelectTrigger>
          <SelectContent>
            {parties.length === 0 ? <div className="px-3 py-2 text-sm text-muted-foreground">No parties. Add a party first.</div>
              : parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.gstin ? ` · ${p.gstin}` : ""}{p.state ? ` · ${p.state}` : ""}</SelectItem>)}
          </SelectContent>
        </Select></div>
    </>
  );
}

export function NotesField({ f, setF, prefix }) {
  return (
    <div className="space-y-2"><Label>Notes</Label>
      <Textarea data-testid={`${prefix}-notes-input`} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Optional" rows={2} /></div>
  );
}

export const emptyInvoice = () => ({ invoice_no: "", date: today(), due_date: "", party_id: "", vendor_id: "", notes: "" });
export { newLine };
